// src/App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { useTheme } from './utils/ThemeContext';
import { Container } from 'react-bootstrap';

// Components
import Home from './components/Home/Home';
import PackingList from './components/PackingLists/PackingList';
import MaintenanceList from './components/MaintenanceLists/MaintenanceList';
import Login from './components/Login';
import Header from './components/Header';
import ErrorBanner from './components/ErrorBanner';

// Styles
import './App.css';

/**
 * Main App component that handles routing and authentication
 * 
 * @returns {JSX.Element} Rendered application
 */
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { theme } = useTheme();
  const location = useLocation();

  /**
   * Set document title based on environment
   */
  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    const titlePrefix = environment === 'development' ? 'DEV - ' : '';
    document.title = `${titlePrefix}Moto Tools`;
  }, []);

  /**
   * Monitor authentication state
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    }, (error) => {
      setError('Authentication error: ' + error.message);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  /**
   * Authentication wrapper component
   * 
   * @param {Object} props - Component props
   * @param {React.ReactNode} props.children - Child components to render if authenticated
   * @returns {JSX.Element} Children or redirect to login
   */
  const RequireAuth = ({ children }) => {
    if (!user) {
      // Store the path they were trying to access
      sessionStorage.setItem('redirectPath', location.pathname);
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  if (loading) {
    return (
      <div className="loading-container d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App" data-bs-theme={theme}>
      <Header user={user} />
      
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
      
      <div className="content-wrapper">
        <main className="main-content">
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
            
            <Route path="/" element={
              <RequireAuth>
                <Container>
                  <Home user={user} />
                </Container>
              </RequireAuth>
            } />
            
            <Route path="/packing-lists" element={
              <RequireAuth>
                <Container>
                  <Home user={user} activeTab="packing" />
                </Container>
              </RequireAuth>
            } />
            
            <Route path="/list/:listId" element={
              <RequireAuth>
                <Container>
                  <PackingList user={user} />
                </Container>
              </RequireAuth>
            } />
            
            <Route path="/shared/:listId" element={
              <Container>
                <PackingList user={null} viewOnly={true} />
              </Container>
            } />
            
            {/* Maintenance routes */}
            <Route path="/maintenance" element={
              <RequireAuth>
                <Container>
                  <Home user={user} activeTab="maintenance" />
                </Container>
              </RequireAuth>
            } />
            
            <Route path="/maintenance/:bikeId" element={
              <RequireAuth>
                <Container>
                  <MaintenanceList user={user} />
                </Container>
              </RequireAuth>
            } />
            
            <Route path="/shared-maintenance/:bikeId" element={
              <Container>
                <MaintenanceList user={null} viewOnly={true} />
              </Container>
            } />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
        {/* Simple footer */}
        <footer className="footer py-3 mt-auto border-top">
          <Container className="text-center">
            <small className="text-muted">
              &copy; {new Date().getFullYear()} OKInvent Limited. All rights reserved.
            </small>
          </Container>
        </footer>
      </div>
    </div>
  );
}

export default App;